import { getOutgoers, getIncomers } from "reactflow";
import {
  NodeTypes as NodeTypeKeys,
  QuestionTypes,
  DEFAULT_LIST_OPTIONS,
} from "./constants";
import { Handle, listHandleId } from "./flowSchema";

export function validateFlow(flowName, nodes, edges, resources = {}) {
  const errors = [];
  const warnings = [];
  const invalidNodeIds = new Set();

  const labelOf = (node) => (node && node.data && node.data.label) || resources["Untitled"];

  const startNodes = nodes.filter((n) => n.type === NodeTypeKeys.START);
  const terminalNodes = nodes.filter((n) => n.type === NodeTypeKeys.TERMINAL);
  const questionNodes = nodes.filter((n) => n.type === NodeTypeKeys.QUESTION);

  if (!flowName || !flowName.trim()) {
    errors.push({ message: resources["FlowMustHaveName"] });
  }

  if (startNodes.length > 1) {
    startNodes.forEach((n) => invalidNodeIds.add(n.id));
    errors.push({ message: resources["FlowMustHaveExactlyOneStartNode"] });
  }

  if (terminalNodes.length === 0) {
    errors.push({ message: resources["FlowMustHaveEndNode"] });
  }

  if (questionNodes.length === 0) {
    errors.push({ message: resources["FlowMustHaveQuestion"] });
  }

  const startNode = startNodes[0];
  if (startNode) {
    const startOutgoers = getOutgoers(startNode, nodes, edges);
    if (startOutgoers.length === 0) {
      invalidNodeIds.add(startNode.id);
      errors.push({
        nodeId: startNode.id,
        message: resources["StartNodeMustBeConnected"],
      });
    }
  }

  questionNodes.forEach((node) => {
    if (!node.data.label) {
      invalidNodeIds.add(node.id);
      errors.push({
        nodeId: node.id,
        message: resources["QuestionNeedsLabel"].replace("#Label#", labelOf(node)),
      });
    }

    if (!node.data.prompt || !node.data.prompt.trim()) {
      invalidNodeIds.add(node.id);
      errors.push({
        nodeId: node.id,
        message: resources["QuestionNeedsPrompt"].replace("#Label#", labelOf(node)),
      });
    }

    if (!node.data.section) {
      invalidNodeIds.add(node.id);
      errors.push({
        nodeId: node.id,
        message: resources["QuestionNeedsSection"].replace("#Label#", labelOf(node)),
      });
    }

    const outgoers = getOutgoers(node, nodes, edges);
    const outEdges = edges.filter((e) => e.source === node.id);

    if (node.data.questionType === QuestionTypes.YES_NO) {
      const hasYes = outEdges.some((e) => e.sourceHandle === Handle.YES);
      const hasNo = outEdges.some((e) => e.sourceHandle === Handle.NO);

      if (!hasYes && !hasNo) {
        invalidNodeIds.add(node.id);
        errors.push({
          nodeId: node.id,
          message: resources["YesNoQuestionNoBranches"].replace("#Label#", labelOf(node)),
        });
      } else if (!hasYes) {
        invalidNodeIds.add(node.id);
        errors.push({
          nodeId: node.id,
          message: resources["YesNoQuestionMissingYes"].replace("#Label#", labelOf(node)),
        });
      } else if (!hasNo) {
        invalidNodeIds.add(node.id);
        errors.push({
          nodeId: node.id,
          message: resources["YesNoQuestionMissingNo"].replace("#Label#", labelOf(node)),
        });
      }
    } else if (node.data.questionType === QuestionTypes.LIST) {
      const listOptions = node.data.listOptions || DEFAULT_LIST_OPTIONS;

      if (listOptions.some((opt) => !opt || !opt.trim())) {
        invalidNodeIds.add(node.id);
        errors.push({
          nodeId: node.id,
          message: resources["ListOptionsNeedLabels"].replace("#Label#", labelOf(node)),
        });
      }

      const missingOptions = [];

      listOptions.forEach((opt, i) => {
        const hasEdge = outEdges.some((e) => e.sourceHandle === listHandleId(i));
        if (!hasEdge) missingOptions.push(opt);
      });

      if (missingOptions.length === listOptions.length) {
        invalidNodeIds.add(node.id);
        errors.push({
          nodeId: node.id,
          message: resources["ListQuestionNoOptions"].replace("#Label#", labelOf(node)),
        });
      } else if (missingOptions.length > 0) {
        invalidNodeIds.add(node.id);
        errors.push({
          nodeId: node.id,
          message: resources["ListQuestionMissingOptions"]
            .replace("#Label#", labelOf(node))
            .replace("#Options#", missingOptions.join(", ")),
        });
      }
    } else {
      if (outgoers.length === 0) {
        invalidNodeIds.add(node.id);
        errors.push({
          nodeId: node.id,
          message: resources["QuestionNoOutgoing"].replace("#Label#", labelOf(node)),
        });
      }
    }

    const incomers = getIncomers(node, nodes, edges);
    if (incomers.length === 0) {
      invalidNodeIds.add(node.id);
      errors.push({
        nodeId: node.id,
        message: resources["QuestionNotReachable"].replace("#Label#", labelOf(node)),
      });
    }
  });

  terminalNodes.forEach((node) => {
    const incomers = getIncomers(node, nodes, edges);
    if (incomers.length === 0) {
      invalidNodeIds.add(node.id);
      errors.push({
        nodeId: node.id,
        message: resources["EndNodeNotReachable"].replace("#Label#", labelOf(node)),
      });
    }

    if (!node.data.label) {
      invalidNodeIds.add(node.id);
      warnings.push({
        nodeId: node.id,
        message: resources["EndNodeNeedsLabel"].replace("#Label#", labelOf(node)),
      });
    }
  });

  if (startNode && errors.length === 0) {
    const reachable = new Set();
    const visited = new Set();

    function walk(nodeId, path) {
      if (visited.has(nodeId)) {
        if (path.includes(nodeId)) {
          const cycleNode = nodes.find((n) => n.id === nodeId);
          invalidNodeIds.add(nodeId);
          errors.push({
            nodeId,
            message: resources["CycleDetected"].replace("#Label#", labelOf(cycleNode)),
          });
        }
        return;
      }
      visited.add(nodeId);
      reachable.add(nodeId);

      const node = nodes.find((n) => n.id === nodeId);
      if (!node) return;

      const outgoers = getOutgoers(node, nodes, edges);
      outgoers.forEach((next) => walk(next.id, [...path, nodeId]));
    }

    walk(startNode.id, []);

    nodes.forEach((node) => {
      if (!reachable.has(node.id)) {
        invalidNodeIds.add(node.id);
        errors.push({
          nodeId: node.id,
          message: resources["NodeNotReachableFromStart"].replace("#Label#", labelOf(node)),
        });
      }
    });

    const allPathsEnd = checkAllPathsReachTerminal(startNode, nodes, edges);
    if (!allPathsEnd.valid) {
      allPathsEnd.deadEnds.forEach((nodeId) => {
        invalidNodeIds.add(nodeId);
        const node = nodes.find((n) => n.id === nodeId);
        errors.push({
          nodeId,
          message: resources["PathNeverReachesEnd"].replace("#Label#", labelOf(node)),
        });
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    invalidNodeIds: Array.from(invalidNodeIds),
  };
}

function checkAllPathsReachTerminal(startNode, nodes, edges) {
  const deadEnds = [];

  function canReachTerminal(nodeId, visited) {
    if (visited.has(nodeId)) return true;
    visited.add(nodeId);

    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return false;
    if (node.type === NodeTypeKeys.TERMINAL) return true;

    const outgoers = getOutgoers(node, nodes, edges);
    if (outgoers.length === 0) return false;

    return outgoers.every((next) => canReachTerminal(next.id, new Set(visited)));
  }

  const questionNodes = nodes.filter((n) => n.type === NodeTypeKeys.QUESTION);
  questionNodes.forEach((node) => {
    const outgoers = getOutgoers(node, nodes, edges);
    if (outgoers.length > 0 && !canReachTerminal(node.id, new Set())) {
      deadEnds.push(node.id);
    }
  });

  return { valid: deadEnds.length === 0, deadEnds };
}

function isReachable(fromId, toId, edges) {
  const visited = new Set();
  const stack = [fromId];
  while (stack.length > 0) {
    const id = stack.pop();
    if (id === toId) return true;
    if (visited.has(id)) continue;
    visited.add(id);
    edges.forEach((e) => {
      if (e.source === id) stack.push(e.target);
    });
  }
  return false;
}

// ignoreEdgeId: when relinking an existing edge, it still occupies its own source handle
export function isValidNewConnection(connection, nodes, edges, ignoreEdgeId = null) {
  const sourceNode = nodes.find((n) => n.id === connection.source);
  const targetNode = nodes.find((n) => n.id === connection.target);

  if (!sourceNode || !targetNode) return false;
  if (connection.source === connection.target) return false;
  if (targetNode.type === NodeTypeKeys.START) return false;
  if (sourceNode.type === NodeTypeKeys.TERMINAL) return false;

  const existingEdge = edges.find(
    (e) =>
      e.id !== ignoreEdgeId &&
      e.source === connection.source &&
      e.sourceHandle === connection.sourceHandle
  );
  if (existingEdge) return false;

  if (sourceNode.type === NodeTypeKeys.START && targetNode.type === NodeTypeKeys.TERMINAL) {
    return false;
  }

  // an edge to an ancestor would close a cycle
  const remainingEdges = ignoreEdgeId ? edges.filter((e) => e.id !== ignoreEdgeId) : edges;
  if (isReachable(connection.target, connection.source, remainingEdges)) return false;

  return true;
}
